// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-PERF-3 Phase 1 — in-memory SWR (stale-while-revalidate) cache for the four list screens
// (Prescriptions /rx, Patients /patients, Providers /prescribers, Orders /shipping/shipments)
// plus intent-based prefetch of the Prescriptions queue.
//
// Hard rules (round table, docs/design/D-PERF-3-predictive-prefetch-roundtable.md):
//   - MEMORY ONLY. Never localStorage/sessionStorage/IndexedDB/service-worker (PHI must not persist).
//   - Serving from cache NEVER suppresses refresh: a cached serve always triggers a background
//     revalidate that updates the cache (screens pick it up on their own reload/poll/next mount).
//   - Flushed whenever the session token changes (login/logout/workspace switch) — Sasha's
//     cross-tenant-bleed guard.
//   - Prefetch is intent-based (pointer enters the Prescriptions nav item) and throttled — it fires
//     work the user is about to request anyway, honoring Lin's DB-load guardrail.
//
// ROLLBACK (cost spike or any misbehavior): flip the two switches below to false and deploy
// (config-in-code; ~10 min). Full procedure: docs/runbooks/PREFETCH-ROLLBACK.md.

/** Master switch for serve-from-cache on the four list GETs. false = every call hits the network (pre-Phase-1 behavior). */
// Re-enabled 2026-07-24 WITH a size cap (see MAX_CACHEABLE_* below). The keystroke lag came from
// serializing very large list payloads on the main thread, not from caching itself — so we now cache
// everything that is cheap to cache and skip only the oversized payloads.
export const SWR_LIST_CACHE_ENABLED = true
/** Switch for the Prescriptions intent-prefetch. false = no prefetch ever fires. */
export const RX_INTENT_PREFETCH_ENABLED = true

const TTL_MS = 60_000          // cached frame max age; background revalidate refreshes it on every serve
const MAX_ENTRIES = 120        // bound memory; oldest evicted (raised for portfolio-wide coverage)
// TYPING-LAG FIX (Patrick, 2026-07-24): storing a cached frame costs main-thread work proportional to
// payload size (JSON.stringify / reading the body). Small config + lookup responses are microseconds;
// ASP's 21,346-row Prescriptions payload is megabytes, and serializing that on every response blocked
// React — which is what keyboard-to-UI lag feels like. So we skip caching oversized payloads, decided
// by CHEAP O(1) checks BEFORE any serialization: a content-length header, or the item count.
export const MAX_CACHEABLE_BYTES = 262_144   // ~256 KB
export const MAX_CACHEABLE_ITEMS = 750       // rows in a list response
const PREFETCH_THROTTLE_MS = 30_000

type Entry = { body: string; at: number }
const entries = new Map<string, Entry>()
const inflight = new Map<string, Promise<unknown>>()
const stats = { hits: 0, misses: 0, revalidations: 0, prefetches: 0, flushes: 0 }

// Phase 1b (Patrick, 2026-07-20): portfolio-wide coverage. DENY first (never cache), then ALLOW.
// Never cached: auth/session/MFA/SSO, AI generations, e-sign, logs/replay (freshness-expectation
// surfaces), secure-share, and action-style lookups (scan/lookup/writeback are workflow steps, not
// list reads). Detail-by-id charts (/patients/{id} etc.) stay uncached deliberately — a stale frame
// on an actively-worked record is Nadia's red line; the ($|\?) anchors below enforce that.
const DENY = [
  /^\/auth\b/, /^\/mfa\b/, /^\/sso\b/,
  /\/ai($|\/|\?)/, /\/esign($|\/|\?)/,
  /^\/system\/logs/, /^\/replay\b/, /^\/secure-share\b/,
  /scan|lookup|writeback/,
  /export|download|\/pdf($|\?)|\/csv($|\?)|\/blob/,
]
const ALLOW = [
  // Exact list surfaces (id-subpaths intentionally NOT matched):
  /^\/(patients|prescribers|rx|users|roles|roadmap|notifications)($|\?)/,
  /^\/shipping\/shipments($|\?)/,
  /^\/views(\/catalog)?($|\?)/,
  /^\/carerx\/(assessments|agencies|drug-matrix)($|\?)/,
  // Read-only families where subpaths are also safe reads (dashboards, lookups, config):
  /^\/dashboards\//, /^\/workflow\//, /^\/tabs\//,
  /^\/pharmacies\/me/, /^\/shipping\/(carriers|label-config)/,
  /^\/carerx\/(pcw|formulary)($|\/|\?)/,
  /^\/ops\//,
]

/** GET paths eligible for list caching. Deny-first, then allow; everything else passes through. */
export function swrListKey(path: string): string | null {
  if (!SWR_LIST_CACHE_ENABLED) return null
  for (const d of DENY) if (d.test(path)) return null
  for (const a of ALLOW) if (a.test(path)) return path
  return null
}

/** O(1) cacheability check on a PARSED payload — never serializes to decide. */
export function isCacheablePayload(data: unknown): boolean {
  const items = (data as { items?: unknown[] } | null)?.items
  if (Array.isArray(items) && items.length > MAX_CACHEABLE_ITEMS) return false
  return true
}

/** O(1) cacheability check on a raw Response — uses the header, never reads the body. */
export function isCacheableResponse(res: Response): boolean {
  const len = Number(res.headers.get('content-length') ?? '0')
  return !(len > MAX_CACHEABLE_BYTES)
}

export function swrGet(key: string): string | null {
  const e = entries.get(key)
  if (!e) return null
  if (Date.now() - e.at > TTL_MS) { entries.delete(key); return null }
  stats.hits++
  return e.body
}

export function swrPut(key: string, body: string): void {
  if (entries.size >= MAX_ENTRIES && !entries.has(key)) {
    const oldest = entries.keys().next().value
    if (oldest !== undefined) entries.delete(oldest)
  }
  entries.set(key, { body, at: Date.now() })
}

/** Run the revalidation at most once concurrently per key. Never throws to the caller. */
export function swrRevalidate(key: string, doNet: () => Promise<unknown>): void {
  if (inflight.has(key)) return
  stats.revalidations++
  const p = doNet().catch(() => { /* background refresh is best-effort */ }).finally(() => inflight.delete(key))
  inflight.set(key, p)
}

export function swrMiss(): void { stats.misses++ }

/** Flush everything — called whenever the session token changes (login/logout/workspace switch). */
export function flushSwrCache(): void {
  entries.clear()
  inflight.clear()
  stats.flushes++
}

/**
 * CV-5 (Custom-views settings staleness): drop every cached frame under a path prefix.
 * client.ts calls this after a successful MUTATION of that resource — without it, the
 * settings page's reload() after create/delete was served the pre-write cached list
 * (SWR revalidates only in the background), so the screen "never" updated.
 */
export function swrInvalidate(prefix: string): void {
  for (const key of [...entries.keys()])
    if (key === prefix || key.startsWith(prefix + '?') || key.startsWith(prefix + '/'))
      entries.delete(key)
}

// ── Prescriptions intent-prefetch ─────────────────────────────────────────────
// StationQueue registers the exact /rx?state= URLs it loads; the Sidebar fires the prefetch when the
// pointer enters the Prescriptions nav item. First-ever visit has nothing registered → no prefetch.
let rememberedRxPaths: string[] = []
let lastPrefetchAt = 0

export function rememberRxListPaths(paths: string[]): void {
  rememberedRxPaths = paths.slice(0, 8)
}

/** Paths worth prefetching right now (registered, stale/missing, throttled). Caller performs the fetches. */
export function rxPrefetchTargets(): string[] {
  if (!RX_INTENT_PREFETCH_ENABLED || !SWR_LIST_CACHE_ENABLED) return []
  const now = Date.now()
  if (now - lastPrefetchAt < PREFETCH_THROTTLE_MS) return []
  const targets = rememberedRxPaths.filter(p => {
    const e = entries.get(p)
    return !e || now - e.at > TTL_MS
  })
  if (targets.length > 0) { lastPrefetchAt = now; stats.prefetches++ }
  return targets
}

// Debug/support visibility (no PHI — counters only).
declare global { interface Window { __owlrxSwrStats?: () => Record<string, number> } }
if (typeof window !== 'undefined') {
  window.__owlrxSwrStats = () => ({ ...stats, entries: entries.size })
}
